import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spin } from 'antd';
import { GoogleMap, Marker, Polygon } from '@react-google-maps/api';
import { Point, Zone } from 'services/ZoneService';
import useStyle from './styles';
import usePolygonColors from './usePolygonColors';

type LatLngLiteral = google.maps.LatLngLiteral;
type LatLng = google.maps.LatLng;

interface MapProps {
  polygons: Zone[];
  initialCenter: LatLngLiteral;
  zoom?: number;
  editablePolygon?: Zone;
  centerPolygon?: Zone;
  isAddZone?: boolean;
  arePolygonsLoading?: boolean;
  onPolygonChange?: (path: Point[]) => void;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '8px',
};

const defaultPolygonOptions = {
  strokeOpacity: 0.7,
  strokeWeight: 3,
  fillOpacity: 0.4,
};

const convertPointToLatLngLiteral = (point: Point): LatLngLiteral => ({
  lat: parseFloat(point.lat),
  lng: parseFloat(point.lng),
});

const getPolygonCenter = (path: LatLngLiteral[]): LatLngLiteral => {
  const bounds = new google.maps.LatLngBounds();
  path.forEach((coord) => bounds.extend(coord));
  return bounds.getCenter().toJSON();
};

const Map: React.FC<MapProps> = ({
  polygons,
  initialCenter,
  zoom = 10,
  editablePolygon = null,
  centerPolygon = null,
  isAddZone = false,
  arePolygonsLoading = false,
  onPolygonChange,
}) => {
  const classes = useStyle();
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRefs = useRef<{ [key: number]: google.maps.Polygon }>({});
  const [mapCenter, setMapCenter] = useState(initialCenter);
  const [centeredPolygonCenter, setCenteredPolygonCenter] = useState<{
    lat: number;
    lng: number;
  }>();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [markersVisible, setMarkersVisible] = useState(true);
  const [currentPolygon, setCurrentPolygon] =
    useState<google.maps.Polygon | null>(null);
  const [isDrawingActive, setIsDrawingActive] = useState(false);
  const [draftPath, setDraftPath] = useState<LatLngLiteral[]>([]);

  const { getColorForPolygon } = usePolygonColors();

  useEffect(() => {
    if (mapLoaded && isAddZone) {
      setIsDrawingActive(true);
    }
  }, [mapLoaded, isAddZone]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({
      draggableCursor: isDrawingActive ? 'crosshair' : undefined,
    });
  }, [isDrawingActive]);

  const handlePolygonChange = useCallback(
    (polygon: google.maps.Polygon) => {
      if (onPolygonChange && polygon) {
        const path = polygon
          .getPath()
          .getArray()
          .map(
            (latLng: LatLng): Point => ({
              lat: latLng.lat().toString(),
              lng: latLng.lng().toString(),
            }),
          );
        if (path.length) onPolygonChange(path);
      }
    },
    [onPolygonChange],
  );

  const setPolygonListeners = useCallback(
    (polygon: google.maps.Polygon) => {
      google.maps.event.addListener(polygon.getPath(), 'set_at', () =>
        handlePolygonChange(polygon),
      );
      google.maps.event.addListener(polygon.getPath(), 'insert_at', () =>
        handlePolygonChange(polygon),
      );
      google.maps.event.addListener(polygon.getPath(), 'remove_at', () =>
        handlePolygonChange(polygon),
      );
      setCurrentPolygon(polygon);
      handlePolygonChange(polygon);
    },
    [handlePolygonChange],
  );

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoaded(true);
    google.maps.event.addListener(map, 'zoom_changed', () =>
      setMarkersVisible(map.getZoom()! >= 8),
    );
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
    setMapLoaded(false);
    Object.values(polygonRefs.current).forEach((polygon) => {
      polygon.setMap(null);
    });
  }, [polygonRefs]);

  const adjustMapBoundsForZone = useCallback(
    (zone: Zone) => {
      if (!mapRef.current) return;

      const bounds = new google.maps.LatLngBounds();

      const extendBoundsForPath = (path: Point[]) => {
        path.forEach((point) =>
          bounds.extend(convertPointToLatLngLiteral(point)),
        );
      };

      if (zone.path && zone.path.length > 0) {
        extendBoundsForPath(zone.path);
      }

      if (zone.childrenZones && zone.childrenZones.length > 0) {
        (zone.childrenZones as Zone[]).forEach((childZone: Zone) => {
          if (childZone.path && childZone.path.length > 0) {
            extendBoundsForPath(childZone.path);
          }
        });
      }

      mapRef.current.fitBounds(bounds);
      const center = {
        lat: bounds.getCenter().lat(),
        lng: bounds.getCenter().lng(),
      };
      setMapCenter(center);
      setCenteredPolygonCenter(center);
    },
    [mapRef],
  );

  const renderPolygons = useCallback(() => {
    if (!polygons.length) return null;

    return polygons.flatMap((zone: Zone) => {
      const paths = [
        zone.path,
        ...((zone.childrenZones as Zone[])?.map((c: Zone) => c.path) || []),
      ]
        .filter(Boolean)
        .map((p) => (p ? p.map(convertPointToLatLngLiteral) : []));

      return paths.map((path, _id) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${zone.id}-${_id}`}>
          <Polygon
            paths={path}
            options={{
              ...defaultPolygonOptions,
              strokeColor: getColorForPolygon(zone.id!),
              fillColor: getColorForPolygon(zone.id!),
            }}
            onLoad={(polygon) => {
              polygonRefs.current[zone.id!] = polygon;
            }}
            onUnmount={() => delete polygonRefs.current[zone.id!]}
          />
          {markersVisible && (
            <Marker
              position={getPolygonCenter(path)}
              options={{ optimized: true }}
              label={{
                className: classes.marker,
                text: zone.fullName?.toUpperCase() ?? '',
              }}
              icon={{ url: '', scaledSize: new google.maps.Size(0, 0) }}
            />
          )}
        </React.Fragment>
      ));
    });
  }, [polygons, getColorForPolygon, markersVisible, classes.marker]);

  const addEditablePolygon = useCallback(
    (polygon: Zone) => {
      if (!mapRef.current || !polygon.path) return;
      const newPolygon = new google.maps.Polygon({
        paths: polygon.path.map(convertPointToLatLngLiteral),
        strokeColor: getColorForPolygon(polygon.id!),
        fillColor: getColorForPolygon(polygon.id!),
        editable: true,
        ...defaultPolygonOptions,
      });
      const newMarker = new google.maps.Marker({
        position: getPolygonCenter(
          newPolygon
            .getPath()
            .getArray()
            .map(
              (latLng: LatLng): LatLngLiteral => ({
                lat: latLng.lat(),
                lng: latLng.lng(),
              }),
            ),
        ),
        optimized: true,
        label: {
          className: classes.marker,
          text: polygon.fullName?.toUpperCase() ?? '',
        },
        icon: {
          url: '',
          scaledSize: new google.maps.Size(0, 0),
        },
      });

      setPolygonListeners(newPolygon);
      adjustMapBoundsForZone(polygon);
      newPolygon.setMap(mapRef.current);
      newMarker.setMap(mapRef.current);
    },
    [setPolygonListeners, getColorForPolygon, adjustMapBoundsForZone, mapRef],
  );

  useEffect(() => {
    if (mapLoaded && editablePolygon && !currentPolygon) {
      addEditablePolygon(editablePolygon);
    }
  }, [mapLoaded, polygons, editablePolygon]);

  useEffect(() => {
    if (mapLoaded && centerPolygon) {
      adjustMapBoundsForZone(centerPolygon);
    }
  }, [mapLoaded, centerPolygon]);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!isDrawingActive || !event.latLng) return;
      setDraftPath((prev) => [...prev, event.latLng!.toJSON()]);
    },
    [isDrawingActive],
  );

  const completePolygon = useCallback(() => {
    if (!mapRef.current || draftPath.length < 3) return;
    const newPolygon = new google.maps.Polygon({
      paths: draftPath,
      editable: true,
      ...defaultPolygonOptions,
    });
    newPolygon.setMap(mapRef.current);
    setIsDrawingActive(false);
    setDraftPath([]);
    setPolygonListeners(newPolygon);
  }, [draftPath, mapRef, setPolygonListeners]);

  const resetPolygon = useCallback(() => {
    if (currentPolygon) {
      currentPolygon.setMap(null);
      setCurrentPolygon(null);
      if (onPolygonChange) onPolygonChange([]);
      if (isAddZone) {
        setDraftPath([]);
        setIsDrawingActive(true);
      } else if (editablePolygon) {
        addEditablePolygon(editablePolygon);
      }
    }
  }, [
    currentPolygon,
    onPolygonChange,
    isAddZone,
    editablePolygon,
    addEditablePolygon,
  ]);

  if (editablePolygon && isAddZone) {
    console.error('Trying both to edit a zone and create one.');
    return null;
  }

  if (arePolygonsLoading)
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div
          style={{
            position: 'absolute',
            zIndex: 1,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin tip="Cargando mapa..." size="large" />
        </div>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={zoom}
        />
      </div>
    );

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={handleMapClick}
      onMouseUp={() => {
        if (mapRef.current) setMapCenter(mapRef.current.getCenter()!.toJSON());
      }}
    >
      {polygons && renderPolygons()}
      {isDrawingActive && draftPath.length >= 1 && (
        <Polygon
          paths={draftPath}
          options={{
            ...defaultPolygonOptions,
            strokeColor: '#2196F3',
            fillColor: '#2196F3',
            editable: false,
            clickable: false,
          }}
        />
      )}
      {isDrawingActive && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            display: 'flex',
            gap: '8px',
          }}
        >
          {draftPath.length > 0 && (
            <Button
              size="large"
              onClick={() => setDraftPath((prev) => prev.slice(0, -1))}
            >
              Deshacer punto
            </Button>
          )}
          {draftPath.length >= 3 && (
            <Button size="large" type="primary" onClick={completePolygon}>
              Completar polígono
            </Button>
          )}
        </div>
      )}
      {(isAddZone || !!editablePolygon) && !isDrawingActive && (
        <Button
          size="large"
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '10px',
            zIndex: 1,
            padding: '0 20px',
          }}
          onClick={resetPolygon}
        >
          Resetear polígono
        </Button>
      )}
      {centerPolygon && centeredPolygonCenter !== mapCenter && (
        <Button
          size="large"
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '10px',
            zIndex: 1,
            padding: '0 20px',
          }}
          onClick={() => adjustMapBoundsForZone(centerPolygon)}
        >
          Volver a centrar
        </Button>
      )}
    </GoogleMap>
  );
};

export default React.memo(Map);
